<template>
  <div
    class="page_container"
    :style="{ flexDirection: isMobile ? 'column' : 'row' }"
  >
    <!-- Barra superior para móviles -->
    <div
      v-if="isMobile && mountMobileMenu"
      class="mobile-header"
    >
      <button
        class="hamburger-menu-btn"
        :class="{ active: showMobileMenu }"
        @click="toggleMobileMenu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
      <div class="mobile-header-logo">
        <img
          :src="logoSrc"
          alt="Logo"
          class="header-logo"
          @click="$router.push('/dashboard')"
        />
      </div>
    </div>

    <!-- Menú lateral para desktop -->
    <menu-container
      v-if="!isMobile"
      class="desktop-menu"
    ></menu-container>

    <!-- Menú móvil overlay -->
    <div
      v-if="isMobile"
      class="mobile-menu-overlay"
      :class="{ active: showMobileMenu }"
      @click="closeMobileMenu"
    >
      <div
        class="mobile-menu-container"
        @click.stop
      >
        <menu-container
          class="mobile-menu"
          @menu-item-click="closeMobileMenu"
        ></menu-container>
      </div>
    </div>

    <!-- Contenido principal -->
    <main-container
      class="main-content"
      :class="{ 'mobile-layout': isMobile }"
    ></main-container>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { watch } from 'vue';
import MenuContainer from './menu/menu_container.vue';
import MainContainer from './main/main_container.vue';
import { useDeviceDetection } from '@/shared/composables/use_device_detection.js';
import { useMobileMenu } from '@/shared/composables/use_mobile_menu.js';
import Isologotipo from '@/assets/images/isologotipo.png';

const router = useRouter();
const { shouldShowMobileView, screenWidth, isMobile, isTablet, isDesktop, getDeviceInfo } =
  useDeviceDetection();
const { showMobileMenu, toggleMobileMenu, closeMobileMenu, mountMobileMenu } = useMobileMenu();
const logoSrc = Isologotipo;

// Función de backup simple basada solo en ancho de pantalla
const isSimpleMobile = () => {
  return screenWidth.value <= 768;
};

// Debug temporal - eliminar después
if (import.meta.env.DEV) {
  watch(
    [screenWidth, isMobile, isTablet, isDesktop],
    () => {
      console.log('🔍 Device Detection Debug:', {
        screenWidth: screenWidth.value,
        isMobile: isMobile.value,
        isTablet: isTablet.value,
        isDesktop: isDesktop.value,
        shouldShowMobileView: shouldShowMobileView(),
        deviceInfo: getDeviceInfo.value,
      });
    },
    { immediate: true },
  );
}
</script>

<style scoped>
.page_container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

/* Barra superior móvil */
.mobile-header {
  display: flex;
  align-items: center;
  height: 60px;
  background: #409eff;
  padding: 0 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  position: relative;
}

.mobile-header-logo {
  flex: 1;
  display: flex;
  justify-content: center;
  margin-right: 48px; /* Compensar el ancho del botón hamburguesa */
}

.header-logo {
  height: 40px;
  cursor: pointer;
}

/* Botón hamburguesa */
.hamburger-menu-btn {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.hamburger-menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.hamburger-menu-btn span {
  display: block;
  width: 24px;
  height: 3px;
  background-color: white;
  margin: 3px 0;
  transition: all 0.3s ease;
  border-radius: 2px;
}

/* Animación del botón hamburguesa */
.hamburger-menu-btn.active span:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.hamburger-menu-btn.active span:nth-child(2) {
  opacity: 0;
}

.hamburger-menu-btn.active span:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Overlay del menú móvil */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.mobile-menu-overlay.active {
  opacity: 1;
  visibility: visible;
}

.mobile-menu-container {
  width: 280px;
  height: 100%;
  background: white;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  overflow-y: auto;
}

.mobile-menu-overlay.active .mobile-menu-container {
  transform: translateX(0);
}

/* Estilos para desktop */
.desktop-menu {
  width: auto;
  flex-shrink: 0;
}

/* Contenido principal */
.main-content {
  flex: 1;
  overflow: hidden;
}

.main-content.mobile-layout {
  height: calc(100vh - 60px);
}

/* Ajustes para desktop */
@media (min-width: 769px) {
  /* .page_container {
    flex-direction: row;
  }
   */
  .main-content {
    height: 100vh;
  }
}

/* Estilos específicos para el menú dentro del overlay móvil */
.mobile-menu :deep(.menu_container) {
  height: 100%;
}

.mobile-menu :deep(.arrow_menu_container) {
  display: none; /* Ocultar botón de colapso en móvil */
}

.mobile-menu :deep(.header-menu) {
  background-color: #409eff;
  border-bottom: 2px solid rgb(241, 241, 241);
}

.mobile-menu :deep(.side-menu) {
  border: none;
}

.mobile-menu :deep(.el-menu-item) {
  padding: 0 20px;
  height: 50px;
  line-height: 50px;
}

/* Asegurar que el menú móvil no esté colapsado */
.mobile-menu :deep(.el-menu--collapse) {
  width: 100% !important;
}

/* Prevenir scroll del body cuando el menú móvil está abierto */
body:has(.mobile-menu-overlay.active) {
  overflow: hidden;
}

/* Estilos para mejorar la experiencia táctil */
.hamburger-menu-btn,
.mobile-menu :deep(.el-menu-item) {
  -webkit-tap-highlight-color: transparent;
}

/* Animaciones más suaves para móvil */
@media (max-width: 768px) {
  .mobile-menu-overlay {
    backdrop-filter: blur(2px);
  }

  .mobile-menu-container {
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
  }
}

/* Mejoras para tablets */
@media (min-width: 769px) and (max-width: 1024px) {
  .mobile-menu-container {
    width: 320px;
  }
}
</style>
